import { ReactNode } from 'react';
import { Paper, alpha, Box, Typography, Button, CircularProgress, Chip } from '@mui/material';
import { AutoAwesome, Refresh, InfoOutline, FilterAltOffOutlined } from '@mui/icons-material';

interface InsightsBannerProps {
    projectCount: number;
    lastUpdatedAt: string | null;
    lastRunAt: string | null;
    showFilterClearButton: boolean;
    isRerunning: boolean;
    longRunning?: boolean;
    projectFilter?: ReactNode;
    dateFilter?: ReactNode;
    onClearFilters?: () => void;
    onRerun: () => void;
}

const InsightsBanner: React.FC<InsightsBannerProps> = ({
    projectCount,
    lastUpdatedAt,
    lastRunAt,
    onRerun,
    isRerunning,
    longRunning = false,
    projectFilter,
    dateFilter,
    onClearFilters,
    showFilterClearButton,
}) => (
    <Paper
        elevation={0}
        sx={{
            p: 1.5,
            pb: 1,
            mb: 1.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    }}
                >
                    <AutoAwesome sx={{ fontSize: 22 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5">Cross-project AI analysis</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        Based on transcripts from {projectCount} project{projectCount === 1 ? '' : 's'}
                        {lastUpdatedAt ? ` · Updated ${lastUpdatedAt}` : lastRunAt ? ` · Last run ${lastRunAt}` : ''}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {projectFilter}
                {dateFilter}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={onRerun}
                    disabled={isRerunning}
                    startIcon={isRerunning ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
                    sx={{ flexShrink: 0, textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
                >
                    {isRerunning ? 'Analyzing…' : 'Run analysis'}
                </Button>
            </Box>
        </Box>

        {/* Row 2: subtle hint / status, separated from row 1 by a single hairline that
            spans the full width (kept on this row, not on each child). */}
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                minWidth: 0,
                mt: 1.25,
                pt: 1.25,
                borderTop: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.08),
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <InfoOutline sx={{ fontSize: 16, flexShrink: 0, color: longRunning ? 'primary.main' : 'gray.100' }} />
                <Typography
                    variant="caption"
                    sx={{ fontSize: 13, color: longRunning ? 'primary.main' : 'gray.100', fontWeight: longRunning ? 600 : 400 }}
                >
                    {longRunning
                        ? 'Insight generation is still in progress — results update automatically when it finishes.'
                        : 'Click "Run analysis" to generate fresh insights for the selected projects and time range.'}
                </Typography>
            </Box>

            {showFilterClearButton && (
                <Chip
                    size="small"
                    disabled={isRerunning}
                    label="Clear filters"
                    variant="outlined"
                    icon={<FilterAltOffOutlined sx={{ fontSize: 16 }} />}
                    onClick={onClearFilters}
                    sx={{ flexShrink: 0, borderRadius: 2 }}
                />
            )}
        </Box>
    </Paper>
);

export default InsightsBanner;
